import React from 'react';
import { useCampaign } from '../../contexts/CampaignContext';
import { formatFormula } from '../../lib/dice';

export default function RollLog({ maxHeight = 320, viewerIdentity = null }) {
  const { diceRolls } = useCampaign();
  const isDm = !!viewerIdentity?.isDm;
  // Hidden rolls only appear in the DM's log.
  const list = Object.values(diceRolls)
    .filter((r) => !r.hidden || isDm)
    .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

  if (list.length === 0) {
    return <div style={styles.empty}>No rolls yet — fire one off from the dice tray.</div>;
  }
  return (
    <div style={{ ...styles.scroll, maxHeight }}>
      {list.map((roll) => <Row key={roll.id} roll={roll} />)}
    </div>
  );
}

function Row({ roll }) {
  const allKept = (roll.dice || []).flatMap((g) => g.kept);
  const allRolled = (roll.dice || []).flatMap((g) => g.results);
  const dropped = allRolled.filter((v, i) => {
    const idxInKept = allKept.indexOf(v);
    if (idxInKept >= 0) { allKept[idxInKept] = '__used__'; return false; }
    return true;
  });
  // Reset because we just mutated a copy reference — recompute fresh:
  const keptDisplay = (roll.dice || []).flatMap((g) => g.kept);
  const time = new Date(roll.createdAt || 0);
  const hh = String(time.getHours()).padStart(2, '0');
  const mm = String(time.getMinutes()).padStart(2, '0');
  const tone =
    roll.crit === 'crit'    ? styles.rowCrit
    : roll.crit === 'fumble'? styles.rowFumble
    : null;

  return (
    <div style={{ ...styles.row, ...tone, ...(roll.hidden ? styles.rowHidden : {}) }}>
      {roll.hidden && <span style={styles.hiddenTag}>🔒</span>}
      <span style={styles.time}>{hh}:{mm}</span>
      <span style={{ ...styles.dot, background: roll.colour || '#888' }} />
      <span style={styles.name}>{roll.playerName || (roll.isDm ? 'DM' : 'Player')}</span>
      <div style={styles.formulaWrap}>
        <span style={styles.formula}>{formatFormula(roll)}</span>
        {roll.label && <span style={styles.label}>{roll.label}</span>}
      </div>
      <span style={styles.dice}>
        [{keptDisplay.join(', ')}]
        {dropped.length > 0 && <span style={styles.dropped}> ({dropped.join(', ')})</span>}
      </span>
      <span style={styles.total}>{roll.total}</span>
      {roll.crit === 'crit'   && <span style={{ ...styles.tag, background: '#facc15', color: '#1a0e00' }}>CRIT</span>}
      {roll.crit === 'fumble' && <span style={{ ...styles.tag, background: '#c03333', color: '#fff' }}>FUMBLE</span>}
    </div>
  );
}

const styles = {
  scroll: { overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4, padding: 8 },
  empty:  { padding: '24px 12px', textAlign: 'center', color: '#6a5d45', fontSize: 12, fontStyle: 'italic' },
  row: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '6px 8px',
    background: 'rgba(20,32,56,0.6)',
    border: '1px solid #2a3552',
    borderRadius: 4,
    fontSize: 12,
    color: '#f0e6d0',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },
  rowCrit:   { borderColor: '#facc15', background: 'rgba(58,42,16,0.65)' },
  rowFumble: { borderColor: '#c03333', background: 'rgba(56,20,20,0.65)' },
  rowHidden: { borderColor: '#a78bfa', background: 'rgba(30,20,55,0.65)', opacity: 0.92 },
  hiddenTag: { color: '#a78bfa', fontSize: 11 },
  time:    { color: '#6a5d45', fontSize: 10, fontFamily: 'monospace' },
  dot:     { width: 8, height: 8, borderRadius: '50%', flexShrink: 0 },
  name:    { fontWeight: 700, color: '#d4a847', maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis' },
  formulaWrap: { flex: 1, display: 'flex', alignItems: 'baseline', gap: 6, overflow: 'hidden', minWidth: 0 },
  formula: { color: '#9a8b6e', fontSize: 11, flexShrink: 0 },
  label:   { color: '#86efac', fontSize: 11, fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  dice:    { color: '#a8b8d8', fontFamily: 'monospace', fontSize: 11 },
  dropped: { color: '#6a5d45', textDecoration: 'line-through' },
  total:   { fontSize: 16, fontWeight: 800, color: '#d4a847', minWidth: 36, textAlign: 'right' },
  tag:     { fontSize: 9, fontWeight: 800, padding: '1px 6px', borderRadius: 2, letterSpacing: '0.06em' },
};
